import React, { useCallback, useMemo } from "react"
import styled from "styled-components"
import { EMPTY_NOTIFICATION_TEMPLATE, useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { addValidatorsNotificationTemplate } from "../../globalStates/ExhibitorValidation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { addElement, removeElementById } from "../../globalStates/utils"
import { newId } from "../../utils/Id"
import Page from "../../components/Page"
import Button from "../../components/Button"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import NotificationTemplatesSection from "./NotificationTemplatesSection"
import { NotificationTemplate } from "../../globalStates/ExhibitorDataTypes"
import { Col, Row } from "react-bootstrap"

const ButtonsBar = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

type NotificationTemplatePackage = "notificationTemplate#small" | "notificationTemplate#medium" | "notificationTemplate#large"

const NotificationTemplatesPage: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const notificationTemplatesStates = useExhibitorDataState().getNotificationTemplates()

    //I know the following part of the code is horrible, but I don't really care, it was urgent
    const canAddNotificationTemplateSmall = useMemo(() => {
        const notificationTemplatesLimit = serviceKeys["notificationTemplate#small"] || 0

        const notificationTemplateStatesForPackage = notificationTemplatesStates.filter(
            (notificationTemplateState) => notificationTemplateState.subelementPackage.get() === "notificationTemplate#small"
        )

        if (notificationTemplateStatesForPackage.length >= notificationTemplatesLimit) return false

        let valid = true
        notificationTemplatesStates?.forEach((notificationTemplate) => {
            const text = notificationTemplate.text.get()

            valid = valid && !!text && text.length >= 0
        })
        return valid
    }, [notificationTemplatesStates, serviceKeys])

    const canAddNotificationTemplateMedium = useMemo(() => {
        const notificationTemplatesLimit = serviceKeys["notificationTemplate#medium"] || 0

        const notificationTemplateStatesForPackage = notificationTemplatesStates.filter(
            (notificationTemplateState) => notificationTemplateState.subelementPackage.get() === "notificationTemplate#medium"
        )

        if (notificationTemplateStatesForPackage.length >= notificationTemplatesLimit) return false

        let valid = true
        notificationTemplatesStates?.forEach((notificationTemplate) => {
            const text = notificationTemplate.text.get()

            valid = valid && !!text && text.length >= 0
        })
        return valid
    }, [notificationTemplatesStates, serviceKeys])

    const canAddNotificationTemplateLarge = useMemo(() => {
        const notificationTemplatesLimit = serviceKeys["notificationTemplate#large"] || 0

        const notificationTemplateStatesForPackage = notificationTemplatesStates.filter(
            (notificationTemplateState) => notificationTemplateState.subelementPackage.get() === "notificationTemplate#small"
        )

        if (notificationTemplateStatesForPackage.length >= notificationTemplatesLimit) return false

        let valid = true
        notificationTemplatesStates?.forEach((notificationTemplate) => {
            const text = notificationTemplate.text.get()

            valid = valid && !!text && text.length >= 0
        })
        return valid
    }, [notificationTemplatesStates, serviceKeys])

    const onAddNotificationTemplate = useCallback(
        (shopPackage: NotificationTemplatePackage) => {
            addElement<NotificationTemplate>(notificationTemplatesStates, {
                ...EMPTY_NOTIFICATION_TEMPLATE,
                subelementPackage: shopPackage,
                id: newId().toString()
            })
            addValidatorsNotificationTemplate(notificationTemplatesStates[notificationTemplatesStates.length - 1])
        },
        [notificationTemplatesStates]
    )

    const onRemoveLocalNotification = useCallback(
        (jobsId: string) => {
            removeElementById<NotificationTemplate>(notificationTemplatesStates, jobsId)
        },
        [notificationTemplatesStates]
    )

    const packages: NotificationTemplatePackage[] = [
        "notificationTemplate#small",
        "notificationTemplate#medium",
        "notificationTemplate#large"
    ]

    function getTitlePackage(shopPackage: NotificationTemplatePackage): string {
        switch (shopPackage) {
            case "notificationTemplate#small":
                return branding.configuration.notificationTemplatesPage.sctNotificationTemplates.packageNameSmall
            case "notificationTemplate#medium":
                return branding.configuration.notificationTemplatesPage.sctNotificationTemplates.packageNameMedium
            case "notificationTemplate#large":
                return branding.configuration.notificationTemplatesPage.sctNotificationTemplates.packageNameLarge
            default:
                return ""
        }
    }

    return (
        <Page
            title={branding.configuration.notificationTemplatesPage.pageTitle}
            description={branding.configuration.notificationTemplatesPage.pageDescription}
        >
            {packages.map((shopPackage) => {
                if ((serviceKeys[shopPackage] || 0) > 0) return null

                return (
                    <Row style={{ borderBottom: "1px solid #d9d9d9", paddingBottom: "25px", marginBottom: "25px" }}>
                        <Col>
                            <h5 style={{ marginBottom: "25px" }}>{getTitlePackage(shopPackage)}</h5>
                            {notificationTemplatesStates?.map((notificationTemplatesState, index) => (
                                <>
                                    {notificationTemplatesState.subelementPackage.get() === shopPackage ? (
                                        <NotificationTemplatesSection
                                            key={notificationTemplatesState.id.get()}
                                            notificationTemplateState={notificationTemplatesState}
                                            index={index}
                                            onClickRemove={onRemoveLocalNotification}
                                        />
                                    ) : null}
                                </>
                            ))}

                            <ButtonsBar>
                                <Button
                                    id="addNotificationTemplate"
                                    caption={branding.configuration.notificationTemplatesPage.btnAddNotificationTemplates}
                                    inverse
                                    onClick={() => onAddNotificationTemplate(shopPackage)}
                                    disabled={
                                        shopPackage === "notificationTemplate#small"
                                            ? !canAddNotificationTemplateSmall
                                            : shopPackage === "notificationTemplate#medium"
                                            ? !canAddNotificationTemplateMedium
                                            : !canAddNotificationTemplateLarge
                                    }
                                />
                            </ButtonsBar>
                        </Col>
                    </Row>
                )
            })}
        </Page>
    )
}

export default NotificationTemplatesPage
